import type { PlatformStats, PlatformTenantRow } from '@/api/platformApi'

export type PlatformReportData = {
  geradoEm: string
  tenants: PlatformTenantRow[]
  stats: PlatformStats | null
}

function formatPtDateTime(d: Date): string {
  return d.toLocaleString('pt-PT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatPtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

function formatMonth(ym: string): string {
  const [y, m] = ym.split('-')
  const months = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ]
  const idx = Number(m) - 1
  return idx >= 0 && idx < 12 ? `${months[idx]} ${y}` : ym
}

function escapeCsv(val: string | number | null | undefined): string {
  const s = String(val ?? '')
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function csvLine(cells: (string | number | null | undefined)[]): string {
  return cells.map(escapeCsv).join(';')
}

function sumTenants(rows: PlatformTenantRow[], key: keyof PlatformTenantRow): number {
  return rows.reduce((acc, r) => acc + Number(r[key] ?? 0), 0)
}

export function buildPlatformReportData(
  tenants: PlatformTenantRow[],
  stats: PlatformStats | null
): PlatformReportData {
  return {
    geradoEm: formatPtDateTime(new Date()),
    tenants: [...tenants].sort((a, b) => b.agendamentos_total - a.agendamentos_total),
    stats,
  }
}

export function downloadPlatformReportCsv(data: PlatformReportData): void {
  const lines: string[] = []
  const t = data.stats?.totals
  const n = data.tenants.length
  const ativas = data.tenants.filter((r) => r.ativo).length

  lines.push('Relatório geral da plataforma')
  lines.push(csvLine(['Gerado em', data.geradoEm]))
  lines.push('')

  lines.push('RESUMO GLOBAL')
  lines.push(
    csvLine([
      'Indicador',
      'Valor',
    ])
  )
  lines.push(csvLine(['Contas (total)', t?.tenants_total ?? n]))
  lines.push(csvLine(['Contas ativas', t?.tenants_ativos ?? ativas]))
  lines.push(csvLine(['Contas suspensas', t?.tenants_suspensos ?? n - ativas]))
  lines.push(
    csvLine([
      'Agendamentos (total)',
      t?.agendamentos_total ?? sumTenants(data.tenants, 'agendamentos_total'),
    ])
  )
  lines.push(csvLine(['Utilizadores (app)', t?.usuarios_total ?? sumTenants(data.tenants, 'usuarios_total')]))
  lines.push(
    csvLine(['Profissionais', t?.profissionais_total ?? sumTenants(data.tenants, 'profissionais_total')])
  )
  lines.push(csvLine(['Unidades', t?.unidades_total ?? sumTenants(data.tenants, 'unidades_total')]))
  lines.push(csvLine(['Admins ativos', t?.admins_ativos ?? sumTenants(data.tenants, 'admins_ativos')]))

  if (data.stats) {
    lines.push(csvLine(['Agendamentos últimos 30 dias', data.stats.agendamentosUltimos30d]))
    lines.push(csvLine(['Agendamentos 30 dias anteriores', data.stats.agendamentosPrev30d]))
    lines.push(csvLine(['Agendamentos não cancelados', data.stats.agendamentosNaoCancelados]))
    if (data.stats.feedbacksTotal != null) {
      lines.push(csvLine(['Feedbacks (total)', data.stats.feedbacksTotal]))
    }
    if (data.stats.comprasTotal != null) {
      lines.push(csvLine(['Compras (total)', data.stats.comprasTotal]))
    }
  }

  if (data.stats?.tenantsByMonth?.length) {
    lines.push('')
    lines.push('NOVAS CONTAS POR MÊS (últimos 12 meses)')
    lines.push(csvLine(['Mês', 'Novas contas']))
    for (const p of data.stats.tenantsByMonth) {
      lines.push(csvLine([formatMonth(p.month), p.count]))
    }
  }

  if (data.stats?.agendamentosByMonth?.length) {
    lines.push('')
    lines.push('AGENDAMENTOS POR MÊS (últimos 12 meses)')
    lines.push(csvLine(['Mês', 'Agendamentos']))
    for (const p of data.stats.agendamentosByMonth) {
      lines.push(csvLine([formatMonth(p.month), p.count]))
    }
  }

  lines.push('')
  lines.push('DETALHE POR CONTA')
  lines.push(
    csvLine([
      'ID',
      'Slug',
      'Nome',
      'Estado',
      'Plano',
      'Utilizadores',
      'Admins',
      'Unidades',
      'Profissionais',
      'Agendamentos',
      'Criada em',
      'Atualizada em',
    ])
  )
  for (const r of data.tenants) {
    lines.push(
      csvLine([
        r.id,
        r.slug,
        r.nome,
        r.ativo ? 'Ativa' : 'Suspensa',
        r.plano ?? '',
        r.usuarios_total,
        r.admins_ativos,
        r.unidades_total,
        r.profissionais_total,
        r.agendamentos_total,
        formatPtDate(r.criado_em),
        formatPtDate(r.atualizado_em),
      ])
    )
  }

  const stamp = new Date().toISOString().slice(0, 10)
  const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `relatorio-plataforma-${stamp}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function metricRow(label: string, value: string): string {
  return `<tr><th>${escapeHtml(label)}</th><td class="val">${escapeHtml(value)}</td></tr>`
}

function kpiCard(label: string, value: string, hint?: string): string {
  return `<article class="kpi">
    <span class="kpi-label">${escapeHtml(label)}</span>
    <strong class="kpi-value">${escapeHtml(value)}</strong>
    ${hint ? `<span class="kpi-hint">${escapeHtml(hint)}</span>` : ''}
  </article>`
}

function maxCount(points: { count: number }[]): number {
  return Math.max(1, ...points.map((p) => p.count))
}

function monthBars(title: string, points: { month: string; count: number }[]): string {
  if (!points.length) return ''
  const max = maxCount(points)
  const bars = points
    .map(
      (p) => `<div class="bar-row">
        <span class="bar-label">${escapeHtml(formatMonth(p.month))}</span>
        <div class="bar-track"><div class="bar-fill" style="width:${Math.round((p.count / max) * 100)}%"></div></div>
        <span class="bar-val">${p.count.toLocaleString('pt-PT')}</span>
      </div>`
    )
    .join('')
  return `<section class="chart-card"><h3>${escapeHtml(title)}</h3>${bars}</section>`
}

declare global {
  interface Window {
    __atuaDownloadPlatformReport?: (key: string) => Promise<void>
  }
}

export function registerPlatformReportDownloadHandler(): void {
  if (typeof window === 'undefined') return
  window.__atuaDownloadPlatformReport = async (key: string) => {
    const raw = sessionStorage.getItem(key)
    if (!raw) {
      throw new Error('Dados do relatório expirados. Gere o relatório novamente.')
    }
    await downloadPlatformReportPdf(JSON.parse(raw) as PlatformReportData)
  }
}

export async function downloadPlatformReportPdf(data: PlatformReportData): Promise<void> {
  const html2pdf = (await import('html2pdf.js')).default
  const fullHtml = buildPlatformReportHtml(data)
  const doc = new DOMParser().parseFromString(fullHtml, 'text/html')
  doc.querySelector('.toolbar')?.remove()
  doc.querySelector('.pdf-tip')?.remove()
  doc.querySelectorAll('script').forEach((s) => s.remove())

  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.style.cssText =
    'position:fixed;left:-10000px;top:0;width:1200px;height:900px;border:0;visibility:hidden'
  document.body.appendChild(iframe)

  const frameDoc = iframe.contentDocument
  if (!frameDoc) {
    document.body.removeChild(iframe)
    throw new Error('Não foi possível preparar o PDF.')
  }

  frameDoc.open()
  frameDoc.write('<!DOCTYPE html>' + doc.documentElement.outerHTML)
  frameDoc.close()

  await new Promise<void>((resolve) => {
    const finish = () => resolve()
    iframe.addEventListener('load', finish, { once: true })
    setTimeout(finish, 500)
  })

  const el = frameDoc.querySelector('.wrap') as HTMLElement | null
  if (!el) {
    document.body.removeChild(iframe)
    throw new Error('Conteúdo do relatório indisponível.')
  }

  const stamp = new Date().toISOString().slice(0, 10)
  try {
    await html2pdf()
      .set({
        margin: [6, 6, 6, 6],
        filename: `relatorio-plataforma-${stamp}.pdf`,
        image: { type: 'jpeg', quality: 0.92 },
        html2canvas: { scale: 2, logging: false },
        jsPDF: { unit: 'mm', format: 'a3', orientation: 'landscape' },
      })
      .from(el)
      .save()
  } finally {
    document.body.removeChild(iframe)
  }
}

export function buildPlatformReportHtml(data: PlatformReportData, reportKey?: string): string {
  const storageKey = reportKey ?? ''
  const t = data.stats?.totals
  const n = data.tenants.length
  const ativas = data.tenants.filter((r) => r.ativo).length
  const suspensas = n - ativas

  const summaryRows = [
    metricRow('Contas (total)', String(t?.tenants_total ?? n)),
    metricRow('Contas ativas', String(t?.tenants_ativos ?? ativas)),
    metricRow('Contas suspensas', String(t?.tenants_suspensos ?? suspensas)),
    metricRow(
      'Agendamentos (total)',
      (t?.agendamentos_total ?? sumTenants(data.tenants, 'agendamentos_total')).toLocaleString('pt-PT')
    ),
    metricRow(
      'Utilizadores (app)',
      (t?.usuarios_total ?? sumTenants(data.tenants, 'usuarios_total')).toLocaleString('pt-PT')
    ),
    metricRow(
      'Profissionais',
      (t?.profissionais_total ?? sumTenants(data.tenants, 'profissionais_total')).toLocaleString('pt-PT')
    ),
    metricRow('Unidades', (t?.unidades_total ?? sumTenants(data.tenants, 'unidades_total')).toLocaleString('pt-PT')),
    metricRow('Admins ativos', (t?.admins_ativos ?? sumTenants(data.tenants, 'admins_ativos')).toLocaleString('pt-PT')),
  ]

  if (data.stats) {
    summaryRows.push(
      metricRow('Agendamentos (30 dias)', data.stats.agendamentosUltimos30d.toLocaleString('pt-PT')),
      metricRow('Agendamentos (período anterior)', data.stats.agendamentosPrev30d.toLocaleString('pt-PT')),
      metricRow('Não cancelados', data.stats.agendamentosNaoCancelados.toLocaleString('pt-PT'))
    )
    if (data.stats.feedbacksTotal != null) {
      summaryRows.push(metricRow('Feedbacks', data.stats.feedbacksTotal.toLocaleString('pt-PT')))
    }
    if (data.stats.comprasTotal != null) {
      summaryRows.push(metricRow('Compras', data.stats.comprasTotal.toLocaleString('pt-PT')))
    }
  }

  const agTotal = (t?.agendamentos_total ?? sumTenants(data.tenants, 'agendamentos_total')).toLocaleString(
    'pt-PT'
  )
  const usersTotal = (t?.usuarios_total ?? sumTenants(data.tenants, 'usuarios_total')).toLocaleString('pt-PT')
  const pctAtivas = n > 0 ? Math.round(((t?.tenants_ativos ?? ativas) / n) * 100) : 0

  const kpiHtml = [
    kpiCard('Contas', String(t?.tenants_total ?? n), `${t?.tenants_ativos ?? ativas} ativas · ${pctAtivas}%`),
    kpiCard('Agendamentos', agTotal, data.stats ? `${data.stats.agendamentosUltimos30d} nos últimos 30 dias` : undefined),
    kpiCard('Utilizadores', usersTotal, `${t?.profissionais_total ?? sumTenants(data.tenants, 'profissionais_total')} profissionais`),
    kpiCard('Infraestrutura', String(t?.unidades_total ?? sumTenants(data.tenants, 'unidades_total')), `${t?.admins_ativos ?? sumTenants(data.tenants, 'admins_ativos')} admins`),
  ].join('')

  const tenantRows = data.tenants
    .map(
      (r) => `<tr>
        <td class="num muted">${r.id}</td>
        <td><code class="slug">${escapeHtml(r.slug)}</code></td>
        <td class="name">${escapeHtml(r.nome)}</td>
        <td>${r.ativo ? '<span class="badge on">Ativa</span>' : '<span class="badge off">Suspensa</span>'}</td>
        <td>${escapeHtml(r.plano ?? '—')}</td>
        <td class="num">${r.usuarios_total.toLocaleString('pt-PT')}</td>
        <td class="num">${r.admins_ativos.toLocaleString('pt-PT')}</td>
        <td class="num">${r.unidades_total.toLocaleString('pt-PT')}</td>
        <td class="num">${r.profissionais_total.toLocaleString('pt-PT')}</td>
        <td class="num strong">${r.agendamentos_total.toLocaleString('pt-PT')}</td>
        <td class="date">${escapeHtml(formatPtDate(r.criado_em))}</td>
      </tr>`
    )
    .join('')

  let chartsHtml = ''
  if (data.stats?.agendamentosByMonth?.length || data.stats?.tenantsByMonth?.length) {
    const parts: string[] = []
    if (data.stats.agendamentosByMonth?.length) {
      parts.push(monthBars('Agendamentos por mês (12 meses)', data.stats.agendamentosByMonth))
    }
    if (data.stats.tenantsByMonth?.length) {
      parts.push(monthBars('Novas contas por mês', data.stats.tenantsByMonth))
    }
    chartsHtml = `<div class="charts">${parts.join('')}</div>`
  }

  return `<!DOCTYPE html>
<html lang="pt">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Relatório da plataforma — ${escapeHtml(data.geradoEm)}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: "Segoe UI", system-ui, -apple-system, sans-serif;
      color: #0f172a;
      background: linear-gradient(180deg, #f0f9ff 0%, #f8fafc 120px, #fff 280px);
      line-height: 1.5;
    }
    .toolbar {
      position: sticky;
      top: 0;
      z-index: 10;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;
      padding: 12px 24px;
      background: rgba(255,255,255,0.95);
      border-bottom: 1px solid #e2e8f0;
      backdrop-filter: blur(8px);
    }
    .toolbar-hint { font-size: 0.85rem; color: #64748b; flex: 1; min-width: 0; }
    .toolbar-actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
      margin-left: 16px;
    }
    .toolbar-actions .btn-print {
      border-color: #94a3b8;
      background: #f8fafc;
    }
    .pdf-tip {
      width: 100%;
      margin: 0;
      padding: 8px 24px 0;
      font-size: 0.82rem;
      color: #0369a1;
      background: #f0f9ff;
      border-bottom: 1px solid #bae6fd;
      box-sizing: border-box;
    }
    .pdf-tip[hidden] { display: none !important; }
    .toolbar button {
      padding: 9px 18px;
      border-radius: 8px;
      font-size: 0.88rem;
      font-weight: 600;
      cursor: pointer;
      border: 1px solid #cbd5e1;
      background: #fff;
      color: #334155;
    }
    .toolbar button.primary {
      border: none;
      background: linear-gradient(135deg, #0284c7, #0ea5e9);
      color: #fff;
      box-shadow: 0 4px 14px rgba(2, 132, 199, 0.35);
    }
    .wrap { max-width: 1100px; margin: 0 auto; padding: 28px 24px 48px; }
    .hero {
      background: linear-gradient(135deg, #0369a1 0%, #0284c7 45%, #0ea5e9 100%);
      color: #fff;
      border-radius: 16px;
      padding: 28px 32px;
      margin-bottom: 28px;
      box-shadow: 0 12px 40px rgba(2, 132, 199, 0.25);
    }
    .hero-badge {
      display: inline-block;
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      opacity: 0.9;
      margin-bottom: 10px;
    }
    .hero h1 { margin: 0 0 8px; font-size: 1.65rem; font-weight: 800; letter-spacing: -0.02em; }
    .hero p { margin: 0; font-size: 0.95rem; opacity: 0.92; }
    .kpi-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 14px;
      margin-bottom: 28px;
    }
    .kpi {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 16px 18px;
      box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
    }
    .kpi-label { display: block; font-size: 0.78rem; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em; }
    .kpi-value { display: block; font-size: 1.55rem; font-weight: 800; color: #0369a1; margin: 6px 0 4px; letter-spacing: -0.02em; }
    .kpi-hint { font-size: 0.78rem; color: #94a3b8; }
    .section-title {
      font-size: 1.1rem;
      font-weight: 800;
      color: #0f172a;
      margin: 0 0 14px;
      padding-bottom: 8px;
      border-bottom: 2px solid #e0f2fe;
    }
    table.summary {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.88rem;
      background: #fff;
      border-radius: 12px;
      overflow: hidden;
      border: 1px solid #e2e8f0;
      margin-bottom: 28px;
      box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
    }
    table.summary th, table.summary td { padding: 11px 16px; border-bottom: 1px solid #f1f5f9; }
    table.summary th { width: 48%; text-align: left; font-weight: 600; color: #475569; background: #f8fafc; }
    table.summary td.val { font-weight: 700; color: #0f172a; }
    table.summary tr:last-child th, table.summary tr:last-child td { border-bottom: none; }
    .charts {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 16px;
      margin-bottom: 28px;
    }
    .chart-card {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 18px;
      box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
    }
    .chart-card h3 { margin: 0 0 14px; font-size: 0.92rem; font-weight: 700; color: #334155; }
    .bar-row { display: grid; grid-template-columns: 72px 1fr 40px; gap: 8px; align-items: center; margin-bottom: 8px; font-size: 0.78rem; }
    .bar-label { color: #64748b; }
    .bar-track { height: 8px; background: #f1f5f9; border-radius: 4px; overflow: hidden; }
    .bar-fill { height: 100%; background: linear-gradient(90deg, #0284c7, #38bdf8); border-radius: 4px; min-width: 2px; }
    .bar-val { text-align: right; font-weight: 700; color: #0369a1; font-variant-numeric: tabular-nums; }
    .table-wrap {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
    }
    table.data { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
    table.data thead { background: linear-gradient(180deg, #f0f9ff, #e0f2fe); }
    table.data th {
      padding: 10px 12px;
      text-align: left;
      font-weight: 700;
      color: #0369a1;
      border-bottom: 2px solid #bae6fd;
      white-space: nowrap;
    }
    table.data td { padding: 9px 12px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
    table.data tbody tr:nth-child(even) td { background: #fafbfc; }
    table.data tbody tr:hover td { background: #f0f9ff; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .num.strong { font-weight: 700; color: #0369a1; }
    .muted { color: #94a3b8; }
    .name { font-weight: 600; max-width: 180px; }
    .date { color: #64748b; white-space: nowrap; }
    code.slug {
      font-size: 0.78rem;
      background: #f1f5f9;
      padding: 2px 8px;
      border-radius: 6px;
      color: #0c4a6e;
    }
    .badge {
      display: inline-block;
      padding: 3px 10px;
      border-radius: 999px;
      font-size: 0.72rem;
      font-weight: 700;
    }
    .badge.on { background: #dcfce7; color: #166534; }
    .badge.off { background: #fee2e2; color: #991b1b; }
    .footer {
      margin-top: 32px;
      padding-top: 16px;
      border-top: 1px dashed #cbd5e1;
      font-size: 0.78rem;
      color: #94a3b8;
      text-align: center;
    }
    @media print {
      body { background: #fff; }
      .toolbar, .pdf-tip { display: none !important; }
      .wrap { padding: 0; max-width: none; }
      .hero { box-shadow: none; break-after: avoid; }
      .kpi, .chart-card, .table-wrap, table.summary { box-shadow: none; break-inside: avoid; }
      table.data tbody tr:hover td { background: inherit; }
    }
  </style>
</head>
<body>
  <div class="toolbar no-print">
    <span class="toolbar-hint">Pré-visualização do relatório</span>
    <div class="toolbar-actions">
      <button type="button" class="btn-print" id="btn-imprimir">Imprimir</button>
      <button type="button" class="primary" id="btn-guardar-pdf">Guardar PDF</button>
      <button type="button" id="btn-fechar">Fechar</button>
    </div>
  </div>
  <p id="pdf-tip" class="pdf-tip no-print" hidden></p>
  <div class="wrap">
    <header class="hero">
      <span class="hero-badge">Super-admin · Plataforma SaaS</span>
      <h1>Relatório geral da plataforma</h1>
      <p>Gerado em ${escapeHtml(data.geradoEm)} · ${n} conta${n === 1 ? '' : 's'} · documento interno confidencial</p>
    </header>

    <div class="kpi-grid">${kpiHtml}</div>

    <h2 class="section-title">Indicadores detalhados</h2>
    <table class="summary"><tbody>${summaryRows.join('')}</tbody></table>

    ${chartsHtml}

    <h2 class="section-title">Detalhe por conta (${n})</h2>
    <div class="table-wrap">
      <table class="data">
        <thead>
          <tr>
            <th>ID</th><th>Slug</th><th>Nome</th><th>Estado</th><th>Plano</th>
            <th class="num">Utiliz.</th><th class="num">Admins</th><th class="num">Unid.</th>
            <th class="num">Prof.</th><th class="num">Agend.</th><th>Criada</th>
          </tr>
        </thead>
        <tbody>${tenantRows || '<tr><td colspan="11" style="text-align:center;padding:24px;color:#64748b">Sem contas registadas</td></tr>'}</tbody>
      </table>
    </div>

    <p class="footer">GoBooking · Relatório gerado automaticamente pelo painel da plataforma</p>
  </div>
  <script>
    (function () {
      var reportKey = ${JSON.stringify(storageKey)};
      var btnPdf = document.getElementById('btn-guardar-pdf');
      var tip = document.getElementById('pdf-tip');

      function showTip(msg) {
        if (!tip) return;
        tip.textContent = msg;
        tip.hidden = !msg;
      }

      document.getElementById('btn-imprimir').addEventListener('click', function () {
        window.print();
      });

      document.getElementById('btn-fechar').addEventListener('click', function () {
        window.close();
      });

      btnPdf.addEventListener('click', function () {
        if (!reportKey) {
          alert('Chave do relatório em falta. Feche e gere o relatório outra vez.');
          return;
        }
        var opener = window.opener;
        if (!opener || typeof opener.__atuaDownloadPlatformReport !== 'function') {
          alert('Mantenha o painel da plataforma aberto na outra aba e clique em Guardar PDF outra vez.');
          return;
        }

        btnPdf.disabled = true;
        showTip('A gerar PDF… o ficheiro será descarregado automaticamente.');

        opener.__atuaDownloadPlatformReport(reportKey)
          .then(function () {
            showTip('PDF descarregado — verifique a pasta Transferências.');
          })
          .catch(function (err) {
            showTip('');
            alert((err && err.message) ? err.message : 'Erro ao gerar o PDF.');
          })
          .finally(function () {
            btnPdf.disabled = false;
          });
      });
    })();
  </script>
</body>
</html>`
}

function downloadHtmlFile(html: string): void {
  const stamp = new Date().toISOString().slice(0, 10)
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `relatorio-plataforma-${stamp}.html`
  a.click()
  URL.revokeObjectURL(url)
}

export function openPlatformReportDocument(data: PlatformReportData): void {
  const reportKey = `platform-report:${Date.now()}`
  try {
    sessionStorage.setItem(reportKey, JSON.stringify(data))
  } catch {
    /* quota — preview sem download automático */
  }
  const html = buildPlatformReportHtml(data, reportKey)
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)

  const win = window.open(url, '_blank')
  if (!win) {
    URL.revokeObjectURL(url)
    downloadHtmlFile(html)
    throw new Error(
      'O browser bloqueou a nova janela. O relatório foi descarregado como ficheiro HTML — abra-o e use Imprimir → Guardar como PDF.'
    )
  }

  const revoke = () => URL.revokeObjectURL(url)
  win.addEventListener('load', revoke, { once: true })
  setTimeout(revoke, 120_000)
}
